import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  Box,
  Container,
  VStack,
  HStack,
  SimpleGrid,
  Text,
  Heading,
  Button,
  Icon,
  Card,
  CardBody,
  CardHeader,
  useToast,
  useColorModeValue,
  Alert,
  AlertIcon,
  Badge,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Center,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { 
  FaCheck,
  FaLock,
  FaArrowLeft,
  FaCreditCard,
  FaShieldAlt,
  FaBuilding,
  FaStar,
  FaCrown
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

import { getAuthHeaders } from '../utils/apiAuth';
// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');



const CheckoutForm = ({ plan, labData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: labData?.contactName || '',
    email: labData?.email || '',
    phone: labData?.phone || ''
  });
  const toast = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone,
        },
      });

      if (error) {
        throw error;
      }

      // Check if we have lab data for specific lab signup
      if (!labData) {
        throw new Error('Lab information is required to complete the signup process. Please go back and select a lab.');
      }

      // Production payment processing with real Stripe API
      
      const response = await fetch('/api/create-subscription-simple', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          customerInfo: customerInfo,
          labData: labData,
          plan: plan
        }),
      });

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error.message);
      }

      // If payment requires additional action (3D Secure)
      if (result.requiresAction) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          result.clientSecret
        );
        
        if (confirmError) {
          throw confirmError;
        }
      }

      // Payment successful - store subscription info temporarily
      sessionStorage.setItem('pendingLabSubscription', JSON.stringify({
        subscriptionId: result.subscriptionId,
        customerId: result.customerId,
        labData: labData,
        plan: plan,
        customerInfo: customerInfo
      }));

      const chargedText = result.trialDays > 0
        ? `Your ${result.trialDays}-day free trial has started.`
        : `You've been charged $${plan.price} for ${plan.name} plan.`;

      toast({
        title: 'Subscription Created',
        description: `${chargedText} 14-day money-back guarantee. Complete your agreement to activate your account.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Redirect to agreement signing page with lab ID parameter
      navigate(`/lab-agreement?labId=${labData.id}`, { 
        state: { 
          labData: labData,
          plan: plan,
          customerInfo: customerInfo,
          subscriptionId: result.subscriptionId,
          customerId: result.customerId
        }
      });

    } catch (error) {
      toast({
        title: 'Payment Failed',
        description: error.message || 'There was an issue processing your payment.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={6}>
        <VStack spacing={4} w="full">
          <FormControl>
            <FormLabel>Full Name</FormLabel>
            <Input
              value={customerInfo.name}
              onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
              placeholder="John Doe"
              required
            />
          </FormControl>
          
          <FormControl>
            <FormLabel>Email Address</FormLabel>
            <Input
              type="email"
              value={customerInfo.email}
              onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
              placeholder="john@example.com"
              required
            />
          </FormControl>
          
          <FormControl>
            <FormLabel>Phone Number</FormLabel>
            <Input
              type="tel"
              value={customerInfo.phone}
              onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
              placeholder="(555) 123-4567"
              required
            />
          </FormControl>
        </VStack>

        <Box w="full">
          <FormLabel>Payment Information</FormLabel>
          <Box
            p={4}
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
          >
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" fontWeight="medium">
              Secure Payment Processing
            </Text>
            <Text fontSize="sm">
              Your payment information is encrypted and processed securely through Stripe.
              You can cancel your subscription at any time.
            </Text>
          </VStack>
        </Alert>

        <Button
          type="submit"
          colorScheme={plan.color}
          size="lg"
          width="full"
          isLoading={loading}
          loadingText="Processing Payment..."
          isDisabled={!stripe || loading}
          leftIcon={<Icon as={FaLock} />}
          fontWeight="bold"
        >
          Complete Payment - ${plan.price}/{plan.period}
        </Button>
      </VStack>
    </form>
  );
};

export default function LabCheckout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { plan, labData } = location.state || {};
  
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50)',
    'linear(to-br, gray.800, gray.900)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    if (!plan) {
      navigate('/lab-signup');
    }
  }, [plan, navigate]);

  if (!plan) {
    return (
      <Box minHeight="100vh" bg={bgGradient}>
        <Container maxW="container.md" py={16}>
          <Center>
            <Spinner size="xl" color="blue.500" />
          </Center>
        </Container>
      </Box>
    );
  }

  return (
    <Box minHeight="100vh" bg={bgGradient}>
      {/* Header */}
      <Box bg={cardBg} borderBottom="1px" borderColor={borderColor} py={8}>
        <Container maxW="container.lg">
          <HStack spacing={4}>
            <Button
              leftIcon={<Icon as={FaArrowLeft} />}
              variant="ghost"
              onClick={() => {
                // Navigate back with lab data preserved
                if (labData?.id) {
                  navigate(`/lab-signup?labId=${labData.id}`, {
                    state: {
                      labId: labData.id,
                      labData: labData,
                      preselectedLab: true
                    }
                  });
                } else {
                  navigate('/lab-signup');
                }
              }}
            >
              Back to Plans
            </Button>
            <VStack align="start" spacing={1} flex="1">
              <Heading size="lg" color={headingColor}>
                Complete Your Subscription
              </Heading>
              <Text color={textColor}>
                Secure checkout{labData ? ` for ${labData.name}` : ''}
              </Text>
            </VStack>
          </HStack>
        </Container>
      </Box>

      {/* Checkout Content */}
      <Container maxW="container.lg" py={12}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
          {/* Order Summary */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader>
              <Heading size="md" color={headingColor}>
                Order Summary
              </Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                {/* Plan Details */}
                <Box>
                  <HStack justify="space-between" mb={4}>
                    <HStack>
                      <Box
                        bg={`${plan.color}.100`}
                        p={2}
                        borderRadius="md"
                        border="1px"
                        borderColor={`${plan.color}.200`}
                      >
                        <Icon 
                          as={plan.id === 'enterprise' ? FaCrown : plan.id === 'professional' ? FaStar : FaBuilding} 
                          boxSize={4} 
                          color={`${plan.color}.600`} 
                        />
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" color={headingColor}>
                          {plan.name} Plan
                        </Text>
                        {plan.popular && (
                          <Badge colorScheme={plan.color} size="sm">
                            Most Popular
                          </Badge>
                        )}
                      </VStack>
                    </HStack>
                    <Text fontWeight="bold" fontSize="lg" color={`${plan.color}.600`}>
                      ${plan.price}/{plan.period}
                    </Text>
                  </HStack>
                  
                  <Text color={textColor} fontSize="sm" mb={4}>
                    {plan.description}
                  </Text>
                  
                  <List spacing={2}>
                    {plan.features.slice(0, 5).map((feature, index) => (
                      <ListItem key={index}>
                        <HStack>
                          <ListIcon as={FaCheck} color="green.500" />
                          <Text fontSize="sm" color={textColor}>{feature}</Text>
                        </HStack>
                      </ListItem>
                    ))}
                    {plan.features.length > 5 && (
                      <ListItem>
                        <Text fontSize="sm" color={textColor} fontStyle="italic">
                          +{plan.features.length - 5} more features
                        </Text>
                      </ListItem>
                    )}
                  </List>
                </Box>

                <Divider />

                {/* Lab Information */}
                {labData && (
                  <Box>
                    <Text fontWeight="bold" color={headingColor} mb={3}>
                      Laboratory Information
                    </Text>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm" color={textColor}>
                        <strong>Lab Name:</strong> {labData.name || 'Not specified'}
                      </Text>
                      <Text fontSize="sm" color={textColor}>
                        <strong>Contact:</strong> {labData.contact_email || 'Not specified'}
                      </Text>
                      <Text fontSize="sm" color={textColor}>
                        <strong>Email:</strong> {labData.contact_email || 'Not specified'}
                      </Text>
                    </VStack>
                  </Box>
                )}

                <Divider />

                {/* Pricing Breakdown */}
                <Box>
                  <VStack spacing={2}>
                    <HStack justify="space-between" w="full">
                      <Text color={textColor}>Monthly Subscription</Text>
                      <Text color={textColor}>${plan.price}.00</Text>
                    </HStack>
                    <HStack justify="space-between" w="full">
                      <Text color={textColor}>Setup & Processing</Text>
                      <Text color="green.500">$0.00</Text>
                    </HStack>
                    <Divider />
                    <HStack justify="space-between" w="full">
                      <Text fontWeight="bold" color={headingColor}>Due Today</Text>
                      <Text fontWeight="bold" fontSize="lg" color={headingColor}>${plan.price}.00</Text>
                    </HStack>
                    <Text fontSize="xs" color={textColor} textAlign="center">
                      {labData?.trial_days && labData.trial_days > 0
                        ? `${labData.trial_days}-day free trial, then $${plan.price}/month. 14-day money-back guarantee.`
                        : `Charged immediately. Next billing in 28 days. 14-day money-back guarantee.`}
                    </Text>
                  </VStack>
                </Box>

                {/* Security Notice */}
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      <Icon as={FaShieldAlt} mr={2} />
                      Secure & Protected
                    </Text>
                    <Text fontSize="sm">
                      Your information is encrypted and secure. 14-day money-back guarantee. Cancel anytime.
                    </Text>
                  </VStack>
                </Alert>
              </VStack>
            </CardBody>
          </Card>

          {/* Payment Form */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader>
              <HStack>
                <Icon as={FaCreditCard} color="blue.500" />
                <Heading size="md" color={headingColor}>
                  Payment Details
                </Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <Elements stripe={stripePromise}>
                <CheckoutForm plan={plan} labData={labData} />
              </Elements>
            </CardBody>
          </Card>
        </SimpleGrid>
      </Container>
    </Box>
  );
}

